import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Download,
  RefreshCw,
  Search,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import { api } from '../../api';
import { useAuth } from '../../auth';
import { userHasRole } from '../../roles';
import { formatRecordingBytes } from '../RecordingRetentionState';
import { sipManager } from '../SipManager';
import { telephonyApi } from '../telephonyApi';
import {
  telephonyRecoveryAvailability,
  telephonyRecoveryPresentation,
} from '../TelephonyRecoveryPresentation';
import type {
  PbxStatus,
  TelephonyHealthAlert,
  TelephonyQualityDiagnostics,
  WebRtcMediaQuality,
} from '../types';

type DiagnosticTab = 'OVERVIEW' | 'EVENTS' | 'OPERATORS' | 'CALLS' | 'INFRA';
type EventFilter = 'ALL' | 'FAILED' | 'PROCESSED' | 'IGNORED' | 'RECEIVED';
type WebhookEntry = {
  id: string;
  provider: string;
  event_key: string;
  received_at: string;
  processing_status: string;
  attempts: number;
  call_id?: string | null;
  error?: string | null;
  processed_at?: string | null;
  payload_preview?: string | null;
};
type OperatorRow = {
  portal_user_id: string;
  full_name: string;
  email?: string | null;
  extension: string;
  browser_phone_enabled: boolean;
  allow_inbound: boolean;
  allow_outbound: boolean;
  operator_status: string;
  last_registered_at?: string | null;
  last_seen_at?: string | null;
};
type Diagnostics = {
  pbx?: PbxStatus & {
    engine?: string;
    version?: string;
    gatewayMessage?: string;
    registrationMessage?: string;
  };
  users?: { configured?: number; online?: number };
  operators?: OperatorRow[];
  calls?: Record<string, number>;
  callStates?: Array<{ state: string; phase: string; status: string; total: number; latest?: string | null }>;
  recentEvents?: Array<{ event_no: number; event_type: string; state: string; event_at: string; source: string; call_id: string; direction?: string | null; call_status?: string | null }>;
  recordings?: {
    available?: number;
    recording?: number;
    failed?: number;
    deleted?: number;
    managedBytes?: number;
    lastAvailableAt?: string | null;
    latestError?: { message?: string; updatedAt?: string } | null;
    retention?: { mode?: string; retentionDays?: number } | null;
    storage?: { level?: string; availableBytes?: number; usedPercent?: number } | null;
    maintenance?: { candidateCount?: number; candidateBytes?: number; lastError?: string; finishedAt?: string | null } | null;
  };
  webhook?: {
    pending?: number;
    failed?: number;
    failed_recent?: number;
    processed?: number;
    ignored?: number;
    last_received_at?: string | null;
    errors?: WebhookEntry[];
    entries?: WebhookEntry[];
  };
};

const TABS: Array<[DiagnosticTab, string]> = [
  ['OVERVIEW', 'Общее'],
  ['EVENTS', 'События'],
  ['OPERATORS', 'Операторы'],
  ['CALLS', 'Звонки'],
  ['INFRA', 'Инфраструктура'],
];

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('ru-RU', {
    day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function mediaValue(value: number | null | undefined, suffix: string, digits = 0) {
  return value === null || value === undefined || !Number.isFinite(value) ? '—' : `${value.toFixed(digits)}${suffix}`;
}

function routeLabel(value: WebRtcMediaQuality['route']) {
  return ({ TURN_RELAY: 'TURN relay', STUN: 'STUN / srflx', DIRECT: 'Прямой', UNKNOWN: 'Не определён' } as const)[value];
}

function alertSource(value: TelephonyHealthAlert['source']) {
  return ({ SIP: 'FreeSWITCH / SIP', BEELINE: 'Билайн', WEBRTC: 'WebRTC', ICE: 'ICE / TURN' } as const)[value];
}

function qualityLabel(value?: string | null) {
  return value === 'CRITICAL' ? 'Критично' : value === 'WARNING' ? 'Внимание' : value === 'GOOD' ? 'Хорошо' : 'Нет данных';
}

function liveQuality(data: WebRtcMediaQuality) {
  if (!data.updatedAt) return { level: 'idle', title: 'Нет замеров', note: 'Показатели появятся после WebRTC-разговора в этой вкладке.' };
  if (data.iceState === 'failed' || data.connectionState === 'failed') return { level: 'bad', title: 'WebRTC-соединение потеряно', note: 'ICE или PeerConnection перешли в состояние failed.' };
  const loss = Math.max(data.packetLossPercent || 0, data.outboundPacketLossPercent || 0);
  const jitter = Math.max(data.jitterMs || 0, data.outboundJitterMs || 0);
  const bad = loss > 3 || jitter > 60 || (data.rttMs || 0) > 350 || (data.concealmentPercent || 0) > 5;
  const warn = loss > 1 || jitter > 30 || (data.rttMs || 0) > 200 || (data.concealmentPercent || 0) > 2;
  if (bad) return { level: 'bad', title: 'Есть признаки деградации', note: 'Проверьте потери, jitter, RTT и concealment.' };
  if (warn) return { level: 'warn', title: 'Качество требует внимания', note: 'Есть пограничные показатели WebRTC.' };
  return { level: 'good', title: 'Качество WebRTC нормальное', note: 'Критических отклонений не обнаружено.' };
}

function CheckCard({ ready, title, text }: { ready: boolean; title: string; text: string }) {
  return <article className={`telephony-diagnostic-card ${ready ? 'ready' : 'warning'}`}>
    <span>{ready ? <CheckCircle2 /> : <AlertTriangle />}</span>
    <div><b>{title}</b><small>{text}</small></div>
    {ready ? <CheckCircle2 /> : <AlertTriangle />}
  </article>;
}

function DetailItem({ label, value, note }: { label: string; value: ReactNode; note?: ReactNode }) {
  return <div className="telephony-diagnostic-detail-item"><small>{label}</small><b>{value}</b>{note && <em>{note}</em>}</div>;
}

function DetailGrid({ rows, className = '' }: { rows: Array<[string, ReactNode, ReactNode?]>; className?: string }) {
  return <div className={`telephony-diagnostic-details telephony-diagnostic-details-v2 ${className}`}>{rows.map(([label, value, note]) => <DetailItem key={label} label={label} value={value} note={note} />)}</div>;
}

export function TelephonyDiagnosticsPanel({ initialTab = 'OVERVIEW' }: { initialTab?: DiagnosticTab }) {
  const { user } = useAuth();
  const admin = userHasRole(user, 'ADMIN');
  const [tab, setTab] = useState<DiagnosticTab>(initialTab);
  const [expandedEvent, setExpandedEvent] = useState('');
  const [eventFilter, setEventFilter] = useState<EventFilter>('FAILED');
  const [eventSearch, setEventSearch] = useState('');
  const [downloadState, setDownloadState] = useState<'IDLE' | 'PENDING' | 'ERROR'>('IDLE');
  const [mediaQuality, setMediaQuality] = useState<WebRtcMediaQuality>(() => sipManager.getMediaQuality());

  useEffect(() => sipManager.on('mediaQuality', setMediaQuality), []);

  const configQuery = useQuery({ queryKey: ['telephony-browser-config'], queryFn: telephonyApi.config });
  const diagnosticsQuery = useQuery<Diagnostics>({
    queryKey: ['telephony-browser-diagnostics'],
    queryFn: () => api<Diagnostics>('/telephony/browser/diagnostics'),
    enabled: admin,
    retry: false,
    refetchInterval: admin ? 10_000 : false,
  });
  const qualityQuery = useQuery<TelephonyQualityDiagnostics>({
    queryKey: ['telephony-quality-diagnostics'],
    queryFn: telephonyApi.qualityDiagnostics,
    enabled: admin,
    retry: false,
    refetchInterval: admin ? 10_000 : false,
  });
  const repair = useMutation({
    mutationFn: telephonyApi.autoRepair,
    onSuccess: () => {
      void configQuery.refetch();
      void diagnosticsQuery.refetch();
      void qualityQuery.refetch();
    },
  });

  const data = diagnosticsQuery.data;
  const cfg = configQuery.data;
  const pbx = data?.pbx;
  const webhook = data?.webhook;
  const recordings = data?.recordings;
  const quality = qualityQuery.data;
  const qualitySummary = quality?.summary;
  const activeAlerts = (quality?.alerts || []).filter(item => item.status === 'ACTIVE');
  const restoredAlerts = (quality?.alerts || []).filter(item => item.status === 'RESOLVED');
  const events = webhook?.entries || webhook?.errors || [];
  const activeCalls = Number(pbx?.activeCalls ?? data?.calls?.active ?? 0);
  const activeChannels = Number(pbx?.activeChannels ?? 0);
  const recovery = telephonyRecoveryAvailability(activeCalls, activeChannels);
  const recoveryResult = repair.data ? telephonyRecoveryPresentation(repair.data) : null;

  const filteredEvents = useMemo(() => {
    const search = eventSearch.trim().toLowerCase();
    return events.filter(item => {
      if (eventFilter !== 'ALL' && item.processing_status !== eventFilter) return false;
      return !search || [item.provider, item.event_key, item.call_id, item.error, item.payload_preview]
        .some(value => String(value || '').toLowerCase().includes(search));
    });
  }, [events, eventFilter, eventSearch]);

  async function downloadDiagnostics() {
    setDownloadState('PENDING');
    try {
      const downloaded = await telephonyApi.exportDiagnostics();
      const url = URL.createObjectURL(downloaded.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloaded.filename;
      link.click();
      URL.revokeObjectURL(url);
      setDownloadState('IDLE');
    } catch {
      setDownloadState('ERROR');
    }
  }

  const refresh = () => {
    void configQuery.refetch();
    if (admin) {
      void diagnosticsQuery.refetch();
      void qualityQuery.refetch();
    }
  };
  const refreshing = configQuery.isFetching || diagnosticsQuery.isFetching || qualityQuery.isFetching;

  return <section className="surface telephony-stage-panel telephony-diagnostics-panel telephony-diagnostics-v2">
    <div className="telephony-stage-panel-head">
      <div><span className="telephony-stage-icon"><Activity /></span><div><small>СОСТОЯНИЕ СИСТЕМЫ</small><h2>Диагностика</h2><p>FreeSWITCH, Билайн, WebRTC, события и состояния звонков.</p></div></div>
      <button type="button" className="button ghost" disabled={refreshing} onClick={refresh}><RefreshCw className={refreshing ? 'spinning' : ''} /> Обновить</button>
    </div>

    {!admin && <div className="telephony-diagnostic-note"><ShieldCheck /><span><b>Расширенная диагностика скрыта</b><small>Системные данные доступны только администратору.</small></span></div>}

    {admin && <>
      <nav className="telephony-diagnostic-tabs">{TABS.map(([id, label]) => <button type="button" key={id} className={tab === id ? 'active' : ''} onClick={() => setTab(id)}><Activity />{label}{id === 'EVENTS' && <em>{Number(webhook?.failed || 0)}</em>}</button>)}</nav>

      {(diagnosticsQuery.isError || qualityQuery.isError) && <div className="admin-feedback error">Часть диагностических данных временно недоступна.</div>}

      {tab === 'OVERVIEW' && <>
        <section className="telephony-support-actions">
          <div className="telephony-support-actions-copy"><span><Wrench /></span><div><small>БЕЗОПАСНОЕ ОБСЛУЖИВАНИЕ</small><h3>Диагностика и восстановление</h3><p>Активные звонки не прерываются, секреты в архив не включаются.</p></div></div>
          <div className="telephony-support-actions-buttons">
            <button type="button" className="button ghost" disabled={downloadState === 'PENDING'} onClick={() => void downloadDiagnostics()}><Download /> {downloadState === 'PENDING' ? 'Формируем архив…' : 'Скачать диагностику'}</button>
            <button type="button" className="button primary" disabled={repair.isPending || recovery.blocked} onClick={() => repair.mutate()}><Wrench /> {repair.isPending ? 'Проверяем…' : recovery.blocked ? 'Дождитесь завершения звонка' : 'Исправить автоматически'}</button>
          </div>
          {recovery.blocked && <p className="telephony-support-lock"><ShieldCheck /> {recovery.message}</p>}
          {downloadState === 'ERROR' && <div className="admin-feedback error">Не удалось скачать диагностический пакет.</div>}
          {repair.isError && <div className="admin-feedback error">Не удалось выполнить автоматическое восстановление.</div>}
          {recoveryResult && <article className={`telephony-repair-result ${recoveryResult.tone}`}>
            <div className="telephony-repair-result-head"><span>{recoveryResult.tone === 'success' ? <CheckCircle2 /> : <AlertTriangle />}</span><div><b>{recoveryResult.title}</b><small>{recoveryResult.message}</small></div><em>{recoveryResult.statusLabel}</em></div>
            <div className="telephony-repair-steps">{recoveryResult.steps.map(step => <div key={step.code} className={step.tone}><span>{step.tone === 'success' ? <CheckCircle2 /> : <AlertTriangle />}</span><div><b>{step.label}</b><small>{step.message}</small></div><em>{step.statusLabel}</em></div>)}</div>
          </article>}
        </section>

        <div className="telephony-diagnostic-grid">
          <CheckCard ready={Boolean(pbx?.gatewayReachable)} title="FreeSWITCH" text={pbx?.gatewayReachable ? 'ESL и шлюз доступны' : String(pbx?.gatewayMessage || 'Нет связи')} />
          <CheckCard ready={pbx?.registrationStatus === 'REGISTERED'} title="SIP Билайн" text={String(pbx?.registrationStatus || 'UNKNOWN')} />
          <CheckCard ready={Boolean((pbx?.webSocketUrl || cfg?.webSocketUrl || '').startsWith('wss://'))} title="WSS" text="Защищённый браузерный SIP" />
          <CheckCard ready={Boolean(pbx?.turnReady)} title="TURN / ICE" text={pbx?.turnReady ? 'Relay готов' : 'Relay требует проверки'} />
          <CheckCard ready={Number(data?.users?.configured || 0) > 0} title="WebRTC" text={`${data?.users?.online || 0} онлайн · ${data?.users?.configured || 0} настроено`} />
          <CheckCard ready={Number(webhook?.failed_recent || 0) === 0} title="События" text={`${webhook?.pending || 0} в очереди · ${webhook?.failed_recent || 0} свежих ошибок`} />
          <CheckCard ready={Number(recordings?.failed || 0) === 0} title="Записи" text={`${recordings?.available || 0} готово · ${recordings?.failed || 0} ошибок`} />
          <CheckCard ready={!activeAlerts.length} title="Качество звонков" text={`${qualitySummary?.measured_calls || 0} измерено · ${activeAlerts.length} алертов`} />
        </div>

        <DetailGrid rows={[
          ['Активные звонки', activeCalls, 'FreeSWITCH'],
          ['Активные каналы', activeChannels],
          ['Пропущено за 24 часа', data?.calls?.missed_24h || 0],
          ['WebRTC зарегистрировано', pbx?.webRtcRegistered || 0],
          ['Последнее событие', formatDateTime(webhook?.last_received_at)],
          ['Последняя запись', formatDateTime(recordings?.lastAvailableAt)],
          ['Объём записей', formatRecordingBytes(Number(recordings?.managedBytes || 0))],
          ['Свободно для записей', recordings?.storage ? formatRecordingBytes(Number(recordings.storage.availableBytes || 0)) : '—'],
        ]} />
      </>}

      {tab === 'EVENTS' && <div className="telephony-diagnostic-section">
        <div className="telephony-diagnostic-section-head"><div><small>WEBHOOK / СОБЫТИЯ</small><h3>Журнал обработки событий</h3><p>Статусы, Call-ID и безопасные технические фрагменты без секретов.</p></div></div>
        <div className="telephony-event-toolbar">
          <label className="search-field"><Search /><input value={eventSearch} onChange={event => setEventSearch(event.target.value)} placeholder="Поиск по Call-ID, событию или ошибке" /></label>
          <select value={eventFilter} onChange={event => setEventFilter(event.target.value as EventFilter)}><option value="ALL">Все события</option><option value="FAILED">Ошибки</option><option value="RECEIVED">Ожидают</option><option value="PROCESSED">Обработаны</option><option value="IGNORED">Игнорированы</option></select>
          <span>Показано <b>{filteredEvents.length}</b> из <b>{events.length}</b></span>
        </div>
        {!filteredEvents.length ? <div className="telephony-diagnostic-empty"><CheckCircle2 /><div><b>Событий по фильтру нет</b><small>Измените статус или строку поиска.</small></div></div> : <div className="telephony-event-error-list">
          {filteredEvents.map(item => <div className={`telephony-event-error-row status-${item.processing_status.toLowerCase()} ${expandedEvent === item.id ? 'expanded' : ''}`} key={item.id}>
            <button type="button" className="telephony-event-error-main" onClick={() => setExpandedEvent(value => value === item.id ? '' : item.id)}>
              <span>{formatDateTime(item.received_at)}</span><span><b>{item.provider || '—'}</b><small>{item.event_key || 'без event key'}</small></span><span>{item.attempts || 1}</span><span>{item.call_id || '—'}</span><span className="error-copy"><em className={`event-processing-status ${item.processing_status.toLowerCase()}`}>{item.processing_status}</em><small>{item.error || 'Без ошибок'}</small></span><ChevronDown />
            </button>
            {expandedEvent === item.id && <div className="telephony-event-error-detail"><dl><div><dt>ID</dt><dd>{item.id}</dd></div><div><dt>Обработано</dt><dd>{formatDateTime(item.processed_at)}</dd></div></dl>{item.error && <pre>{item.error}</pre>}{item.payload_preview && <pre>{item.payload_preview}</pre>}</div>}
          </div>)}
        </div>}
      </div>}

      {tab === 'OPERATORS' && <div className="telephony-diagnostic-section">
        <div className="telephony-diagnostic-section-head"><div><small>WEBRTC</small><h3>Операторы и регистрации</h3><p>Добавочные, права и последняя активность рабочих мест.</p></div></div>
        <div className="telephony-diagnostic-operator-table">
          <div className="head"><span>Сотрудник</span><span>Добавочный</span><span>Телефон</span><span>Входящие</span><span>Исходящие</span><span>Статус</span><span>Регистрация</span><span>Активность</span></div>
          {(data?.operators || []).map(item => <div className="row" key={item.portal_user_id}><span><b>{item.full_name || 'Без имени'}</b><small>{item.email || ''}</small></span><span>{item.extension || '—'}</span><span>{item.browser_phone_enabled ? 'Включён' : 'Выключен'}</span><span>{item.allow_inbound ? 'Да' : 'Нет'}</span><span>{item.allow_outbound ? 'Да' : 'Нет'}</span><span><em className={`operator-state ${item.operator_status === 'OFFLINE' ? 'offline' : 'online'}`}>{item.operator_status || 'UNKNOWN'}</em></span><span>{formatDateTime(item.last_registered_at)}</span><span>{formatDateTime(item.last_seen_at)}</span></div>)}
          {!data?.operators?.length && <div className="telephony-diagnostic-empty"><Activity /><div><b>Операторы не найдены</b><small>Нет данных о регистрациях.</small></div></div>}
        </div>
      </div>}

      {tab === 'CALLS' && <div className="telephony-diagnostic-section">
        <div className="telephony-diagnostic-section-head"><div><small>ЗВОНКИ / WEBRTC</small><h3>Звонки, записи и качество</h3><p>Фактическое состояние FreeSWITCH и серверные агрегаты качества.</p></div></div>
        <DetailGrid rows={[
          ['Активные звонки', activeCalls],
          ['Сегодня всего', data?.calls?.today_total || 0],
          ['Сегодня входящих', data?.calls?.today_inbound || 0],
          ['Сегодня исходящих', data?.calls?.today_outbound || 0],
          ['Сегодня пропущено', data?.calls?.today_missed || 0],
          ['Записей готово', recordings?.available || 0],
          ['Ошибок записи', recordings?.failed || 0],
          ['Режим хранения', recordings?.retention?.mode === 'ENABLED' ? 'Очистка включена' : 'Только отчёт', `${recordings?.retention?.retentionDays || 180} дней`],
        ]} />

        <section className="telephony-quality-diagnostics">
          <div className="telephony-quality-diagnostics-head"><span><Activity /></span><div><small>АГРЕГИРОВАННАЯ ДИАГНОСТИКА</small><h4>Качество за 24 часа</h4><p>Единый накопительный агрегат на каждый браузерный звонок.</p></div><em className={activeAlerts.length ? 'bad' : 'good'}>{activeAlerts.length ? `${activeAlerts.length} АКТИВНО` : 'БЕЗ АЛЕРТОВ'}</em></div>
          <DetailGrid className="telephony-quality-summary-grid" rows={[
            ['Измерено звонков', qualitySummary?.measured_calls || 0, `${qualitySummary?.samples || 0} замеров`],
            ['С отклонениями', `${Number(qualitySummary?.degraded_percent || 0).toFixed(1)} %`, `${qualitySummary?.critical_calls || 0} критичных`],
            ['Средний RTT', mediaValue(qualitySummary?.avg_rtt_ms, ' мс')],
            ['Средние потери', mediaValue(qualitySummary?.avg_loss_percent, ' %', 2)],
            ['Средний jitter', mediaValue(qualitySummary?.avg_jitter_ms, ' мс', 1)],
            ['Звонки через TURN', `${Number(qualitySummary?.turn_relay_percent || 0).toFixed(1)} %`],
          ]} />
          <div className="telephony-quality-columns">
            <section><h4>Активные алерты <em>{activeAlerts.length}</em></h4><div className="telephony-quality-alert-list">{activeAlerts.map(item => <article className={item.severity.toLowerCase()} key={item.id}><AlertTriangle /><div><b>{item.title}</b><small>{alertSource(item.source)} · {item.message}</small><em>{formatDateTime(item.last_seen_at)}</em></div></article>)}{!activeAlerts.length && <div className="telephony-quality-empty"><CheckCircle2 /><span><b>Активных алертов нет</b><small>Телефония не требует вмешательства.</small></span></div>}</div>
              <h4>Восстановлено за 24 часа <em>{restoredAlerts.length}</em></h4><div className="telephony-quality-alert-list">{restoredAlerts.map(item => <article className="resolved" key={item.id}><CheckCircle2 /><div><b>{item.title}</b><small>{alertSource(item.source)} · {item.message}</small><em>{formatDateTime(item.resolved_at || item.last_seen_at)}</em></div></article>)}{!restoredAlerts.length && <div className="telephony-quality-empty muted"><CheckCircle2 /><span><b>Восстановлений не было</b><small>Закрытых инцидентов за сутки нет.</small></span></div>}</div>
            </section>
            <section><h4>Последние измеренные звонки <em>{quality?.recentCalls.length || 0}</em></h4><div className="telephony-quality-call-list">{(quality?.recentCalls || []).map(item => <article key={item.call_id}><em className={item.worst_level.toLowerCase()}>{qualityLabel(item.worst_level)}</em><span><b>{(item.direction === 'INBOUND' ? item.from_number : item.to_number) || 'Номер не определён'}</b><small>{item.operator_name || 'Оператор не определён'} · {item.sample_count} замеров · {routeLabel(item.last_route)}</small></span><span><b>{mediaValue(item.avg_loss_percent, ' %', 2)}</b><small>потери</small></span><span><b>{mediaValue(item.avg_jitter_ms, ' мс', 1)}</b><small>jitter</small></span><span><b>{mediaValue(item.avg_rtt_ms, ' мс')}</b><small>RTT</small></span><time>{formatDateTime(item.last_sample_at)}</time></article>)}{!quality?.recentCalls.length && <div className="telephony-quality-empty"><Activity /><span><b>Замеров пока нет</b><small>Сводка заполнится после браузерного разговора.</small></span></div>}</div></section>
          </div>
        </section>

        {(() => {
          const verdict = liveQuality(mediaQuality);
          return <section className={`telephony-media-quality ${verdict.level}`}><div className="telephony-media-quality-head"><Activity /><div><small>WEBRTC / ЭТА ВКЛАДКА</small><h4>Качество текущего звонка</h4><p>{verdict.note}</p></div><em className={`media-quality-state ${verdict.level}`}>{mediaQuality.active ? 'LIVE' : 'ПОСЛЕДНИЙ ЗАМЕР'}</em></div><div className="telephony-media-quality-verdict"><b>{verdict.title}</b><small>{mediaQuality.updatedAt ? new Date(mediaQuality.updatedAt).toLocaleTimeString('ru-RU') : 'Ожидание звонка'}</small></div><DetailGrid className="telephony-media-quality-grid" rows={[
            ['Кодек', mediaQuality.codec || '—'],
            ['RTT', mediaValue(mediaQuality.rttMs, ' мс')],
            ['Потери → браузер', mediaValue(mediaQuality.packetLossPercent, ' %', 2)],
            ['Потери → собеседник', mediaValue(mediaQuality.outboundPacketLossPercent, ' %', 2)],
            ['Jitter → браузер', mediaValue(mediaQuality.jitterMs, ' мс', 1)],
            ['Jitter → собеседник', mediaValue(mediaQuality.outboundJitterMs, ' мс', 1)],
            ['Concealment', mediaValue(mediaQuality.concealmentPercent, ' %', 2)],
            ['Маршрут ICE', routeLabel(mediaQuality.route), mediaQuality.protocol || undefined],
          ]} /></section>;
        })()}

        <div className="telephony-diagnostic-two-columns">
          <section><h4>Состояния в базе</h4><div className="telephony-call-state-list">{(data?.callStates || []).map((item, index) => <div key={`${item.state}-${index}`}><span><b>{item.state || '—'}</b><small>{item.status || '—'} · {item.phase || '—'}</small></span><strong>{item.total}</strong><em>{formatDateTime(item.latest)}</em></div>)}</div></section>
          <section><h4>Последние события</h4><div className="telephony-recent-event-list">{(data?.recentEvents || []).map(item => <div key={item.event_no}><span><b>{item.event_type}</b><small>{item.source || '—'} · {item.direction || '—'} · {item.call_status || item.state || '—'}</small></span><em>{formatDateTime(item.event_at)}</em><code>{item.call_id?.slice(0, 8) || '—'}</code></div>)}</div></section>
        </div>
      </div>}

      {tab === 'INFRA' && <div className="telephony-diagnostic-section">
        <div className="telephony-diagnostic-section-head"><div><small>FREE SWITCH / SIP / MEDIA</small><h3>Инфраструктура телефонии</h3><p>Технические параметры без паролей, токенов и приватных ключей.</p></div></div>
        <div className="telephony-infra-grid">{[
          ['Движок', `${String(pbx?.engine || 'FreeSWITCH')} ${String(pbx?.version || '')}`, pbx?.gatewayReachable ? 'ESL доступен' : String(pbx?.gatewayMessage || 'Недоступен')],
          ['SIP Билайн', String(pbx?.registrationStatus || 'UNKNOWN'), String(pbx?.registrationMessage || 'Общий системный trunk')],
          ['WSS endpoint', String(pbx?.webSocketUrl || '—'), `SIP-домен: ${String(pbx?.sipDomain || '—')}`],
          ['TURN / ICE', pbx?.turnReady ? 'Relay готов' : 'Требует проверки', String(pbx?.turnRange || '—')],
          ['RTP диапазон', String(pbx?.rtpRange || '—'), 'Медиа FreeSWITCH'],
          ['WebRTC регистрации', String(pbx?.webRtcRegistered || 0), `${data?.users?.online || 0} операторов online`],
          ['Активные звонки', String(activeCalls), `${activeChannels} активных каналов`],
          ['Последний webhook', formatDateTime(webhook?.last_received_at), `${webhook?.failed || 0} ошибок в журнале`],
        ].map(([label, value, note]) => <section key={label}><span><Activity /></span><div><small>{label}</small><b>{value}</b><p>{note}</p></div></section>)}</div>
      </div>}
    </>}
  </section>;
}
